import React, { useMemo } from 'react';
import { useSelector } from "react-redux";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer, LabelList } from 'recharts';
import typeOfContract from '../../../../constants/typeOfContract';
import { processedRevenueByClient } from './Services/reportRevenue';

const RevenuePerClientGraph = ({ clientInvoices, clientInvoicesLoading, clientInvoicesError, trackReport, trackReportLoading, trackReportError, projectCost, expenses }) => {
  const reportingRevenue = useSelector((state) => state.reportingRevenue);
  const { forex } = useSelector((state) => state.forex);
  const settings = JSON.parse(localStorage.getItem("settings"));

  const setReportingCurrency = useSelector((state) => state.reportingRevenue.revenueCurrency);
  var currencies = "USD";
  if (setReportingCurrency && setReportingCurrency.length === 1) {
    currencies = setReportingCurrency[0];
  } else {
    currencies = settings?.currency || "USD";
  }

  const processedData = useMemo(() => {
    if (!trackReport || !projectCost || !clientInvoices || !expenses || !reportingRevenue || !forex) {
      return [];
    }
    try {
      return processedRevenueByClient(
        trackReport,
        projectCost,
        clientInvoices,
        reportingRevenue,
        currencies,
        forex,
        typeOfContract,
        expenses
      ) || [];
    } catch (error) {
      console.error('Error processing revenue by client data:', error);
      return [];
    }
  }, [trackReport, projectCost, clientInvoices, reportingRevenue, currencies, forex, typeOfContract, expenses]);

  if (clientInvoicesLoading || trackReportLoading) {
    const skeletonRows = 8;
    const rowHeight = 50;
    const baseHeight = 100;
    const skeletonHeight = skeletonRows * rowHeight + baseHeight;
    
    // Fixed widths for skeleton bars to avoid random values
    const barWidths = [45, 60, 35, 55, 40, 50, 38, 48];
    
    return (
      <div className='graph-project-per-hour' style={{ width: '100%', height: Math.max(542, skeletonHeight) }}>
        <div style={{ 
          width: '100%', 
          height: '100%', 
          padding: '20px 42px 5px 30px',
          position: 'relative'
        }}>
          {/* Y-axis skeleton labels */}
          <div style={{ 
            position: 'absolute', 
            left: '0', 
            top: '20px', 
            bottom: '5px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            paddingRight: '10px'
          }}>
            {Array.from({ length: skeletonRows }).map((_, index) => (
              <div
                key={index}
                style={{
                  width: '120px',
                  height: '13px',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '4px',
                  animation: 'pulse 1.5s ease-in-out infinite',
                }}
              />
            ))}
          </div>

          {/* Chart area with bars */}
          <div style={{ 
            marginLeft: '150px',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            paddingTop: '10px',
            paddingBottom: '30px',
            position: 'relative'
          }}>
            {/* Vertical grid lines skeleton */}
            <div style={{ 
              position: 'absolute',
              top: '10px',
              left: '0',
              right: '0',
              bottom: '30px',
              display: 'flex',
              justifyContent: 'space-between'
            }}>
              {Array.from({ length: 6 }).map((_, index) => (
                <div
                  key={index}
                  style={{
                    width: '1px',
                    height: '100%',
                    background: 'repeating-linear-gradient(to bottom, #D6D9DB 0px, #D6D9DB 3px, transparent 3px, transparent 6px)',
                    opacity: 0.3
                  }}
                />
              ))}
            </div>

            {Array.from({ length: skeletonRows }).map((_, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  height: '50px',
                  position: 'relative'
                }}
              >
                {/* Single bar skeleton */}
                <div
                  style={{
                    width: `${barWidths[index] || 45}%`,
                    height: '13px',
                    backgroundColor: '#C6DDFF',
                    opacity: 0.3,
                    borderRadius: '2px',
                    animation: 'pulse 1.5s ease-in-out infinite',
                  }}
                />
              </div>
            ))}
          </div>

          {/* X-axis skeleton ticks */}
          <div style={{ 
            position: 'absolute', 
            bottom: '5px', 
            left: '150px', 
            right: '42px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end'
          }}>
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                style={{
                  width: '40px',
                  height: '12px',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '4px',
                  animation: 'pulse 1.5s ease-in-out infinite',
                }}
              />
            ))}
          </div>
        </div>
        <style>{`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        `}</style>
      </div>
    );
  }

  if (clientInvoicesError || trackReportError) return <div>Error loading data.</div>;
  
  if (!processedData || processedData.length === 0) {
    return <div style={{ width: '100%', height: 542, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No data to display</div>;
  }

  const calculateXTicks = () => {
    if (!processedData || processedData.length === 0) return [0];
    const dataValues = processedData.map(item => item.closedAmount || 0);
    const maxDataValue = Math.max(...dataValues);
    const minDataValue = Math.min(...dataValues);

    // Determine if there are negative values
    const hasNegativeValues = minDataValue < 0;

    // Determine tick interval
    const tickInterval = Math.ceil(Math.max(Math.abs(maxDataValue), Math.abs(minDataValue)) / 10);
    const numOfTicks = Math.min(Math.ceil(Math.max(Math.abs(maxDataValue), Math.abs(minDataValue)) / tickInterval), 10);
    const ticks = [];

    // Generate ticks based on whether there are negative values
    if (hasNegativeValues) {
      const extraNegativeStep = tickInterval;
      for (let i = -numOfTicks - 1; i <= numOfTicks; i++) {
        ticks.push(i * tickInterval);
      }
      ticks.unshift(ticks[0] - extraNegativeStep);
    } else {
      for (let i = 0; i <= numOfTicks; i++) {
        ticks.push(i * tickInterval);
      }
    }

    return ticks;
  }

  const xAxisTicks = calculateXTicks();

  const calculateChartHeight = (dataLength) => {
    const rowHeight = 50;
    const baseHeight = 100;
    return dataLength * rowHeight + baseHeight;
  };

  const calculateLeftMargin = (data) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = '12px Arial';

    const longestWordLength = data.reduce((maxLength, item) => {
      const firstWord = item?.name?.split(' ')[0];
      const wordWidth = context.measureText(firstWord).width;
      return Math.max(maxLength, wordWidth);
    }, 0);

    return Math.max(10, longestWordLength - 150);
  };

  const leftMargin = calculateLeftMargin(processedData);

  return (
    <div className='graph-project-per-hour' style={{ width: '100%', height: Math.max(542, calculateChartHeight(processedData.length)), }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          layout="vertical"
          data={processedData}
          barSize={13}
          margin={{
            top: 20,
            right: 42,
            left: leftMargin,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} vertical={true} />
          <XAxis
            type="number"
            domain={['auto', 'auto']}
            tickFormatter={(tick) => `${new Intl.NumberFormat("en-US", { style: "currency", currency: currencies || 'USD' }).format(tick)}`}
            ticks={xAxisTicks}
          />
          <YAxis
            type="category"
            dataKey="name"
            tickMargin={10}
          />
          <Tooltip formatter={(value) => `${new Intl.NumberFormat("en-US", { style: "currency", currency: currencies || 'USD' }).format(value)}`} />
          <ReferenceLine x={0} stroke="#7393B3" />
          <Bar dataKey="closedAmount" name="Closed amount in default currency" fill="#C6DDFF">
            <LabelList dataKey="closedAmount" position="right" fill="#282828" formatter={(value) => `${new Intl.NumberFormat("en-US", { style: "currency", currency: currencies || 'USD' }).format(value)}`} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default RevenuePerClientGraph;
